"use client";

import React, { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import styles from './UserReferralsOverTimeChart.module.css';
import { gsap } from 'gsap';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

interface UserReferralsOverTimeChartProps {
  chartLabels: string[];
  chartDataPoints: number[];
  isLoading: boolean;
}

const UserReferralsOverTimeChart: React.FC<UserReferralsOverTimeChartProps> = ({ chartLabels, chartDataPoints, isLoading }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading && chartDataPoints && chartDataPoints.length > 0 && chartRef.current) {
      gsap.fromTo(
        chartRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.2 }
      );
    }
  }, [isLoading, chartDataPoints]); // Dependency array updated

  const isSingleDataPoint = chartDataPoints && chartDataPoints.length === 1;

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Your Cumulative Referrals",
        data: chartDataPoints,
        fill: true,
        borderColor: "rgba(54, 162, 235, 0.9)", 
        backgroundColor: "rgba(54, 162, 235, 0.25)",
        tension: 0.25,
        pointBackgroundColor: "rgba(54, 162, 235, 0.9)",
        pointBorderColor: "#1A202C", 
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(54, 162, 235, 1)",
        pointRadius: isSingleDataPoint ? 5 : 3,
        pointHoverRadius: isSingleDataPoint ? 7 : 5,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          color: "#E2E8F0",
          font: { size: 12, family: 'Inter, sans-serif' },
          padding: 20,
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle' as const,
        },
      },
      title: {
        display: true,
        text: "Your Cumulative Referrals Over Time",
        color: "#F7FAFC",
        font: { size: 16, weight: "600" as "600", family: 'Inter, sans-serif' },
        padding: { top: 10, bottom: 25 },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: "rgba(45, 55, 72, 0.95)",
        titleColor: "rgba(54, 162, 235, 1)", 
        bodyColor: "#E2E8F0",
        borderColor: "rgba(54, 162, 235, 0.6)",
        borderWidth: 1,
        padding: 12,
        boxPadding: 4,
        cornerRadius: 6,
        bodySpacing: 5,
        titleFont: { weight: 'bold' as 'bold', family: 'Inter, sans-serif' },
        bodyFont: { family: 'Inter, sans-serif' },
        footerFont: { family: 'Inter, sans-serif' },
        displayColors: false,
      },
    },
    scales: {
      x: {
        type: "time" as const,
        time: {
          unit: "day" as const,
          tooltipFormat: "MMM d, yyyy",
          displayFormats: { day: "MMM d" },
        },
        title: { display: false },
        ticks: {
          color: "#A0AEC0",
          font: { family: 'Inter, sans-serif' },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: false },
        ticks: {
          color: "#A0AEC0",
          font: { family: 'Inter, sans-serif' },
          precision: 0,
          stepSize: chartDataPoints && chartDataPoints.length > 0 && Math.max(...chartDataPoints) < 5 ? 1 : undefined,
        },
        grid: {
          color: "rgba(54, 162, 235, 0.15)", 
          drawBorder: false,
        },
        ...(isSingleDataPoint && chartDataPoints[0] !== undefined && { 
          suggestedMax: chartDataPoints[0] < 2 ? chartDataPoints[0] + 2 : chartDataPoints[0] + Math.max(1, Math.ceil(chartDataPoints[0] * 0.2)) 
        })
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
  };

  if (isLoading) return <p className={styles.loadingText}>Loading your referrals chart...</p>;
  if (!isLoading && (!chartDataPoints || chartDataPoints.length === 0))
    return <p className={styles.noDataText}>You have no referral data to display a chart.</p>;

  return (
    <div className={styles.chartCard} ref={chartRef}>
      <Line options={options as any} data={chartData} />
    </div>
  );
};

export default React.memo(UserReferralsOverTimeChart); 